import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal" // Assuming it's available for reuse
import Button from "aldoo-ra/Button" // Assuming it's available for reuse
import ProgressBar from "aldoo-ra/ProgressBar" // Assuming it's available for reuse
import Checkbox from "../Checkbox" // Assuming you have a Checkbox component created
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"

export default function LocalizationImportDialog({
  isOpen,
  onClose,
  controller,
}) {
  const [filename, setFilename] = useState("")
  const [languages, setLanguages] = useState([]) // Languages from the file
  const [selectAll, setSelectAll] = useState(false) // State for Select All checkbox
  const [missingOnly, setMissingOnly] = useState(false) // Toggle for Missing Only option
  const [existingOnly, setExistingOnly] = useState(false) // Toggle for Missing Only option
  const [allKeys, setAllKeys] = useState(false) // Toggle for All Keys option
  const [selectedLanguages, setSelectedLanguages] = useState([]) // Selected languages
  const [taskProgressId, setTaskProgressId] = useState(null) // TaskProgress ID
  const [numberOfKeys, setNumberOfKeys] = useState(0) // Total number of keys in the file
  const [progress, setProgress] = useState(0) // Progress state
  const [error, setError] = useState("") // Error state
  const [isImporting, setIsImporting] = useState(false) // Loading state for import
  const fileInputRef = useRef(null)

  // Select all languages toggle
  const handleSelectAll = () => {
    setSelectAll(!selectAll)
  }

  // Toggle language selection
  const handleLanguageToggle = (code) => {
    if (selectedLanguages.includes(code)) {
      setSelectedLanguages(selectedLanguages.filter((lang) => lang !== code))
    } else {
      setSelectedLanguages([...selectedLanguages, code])
    }
  }

  const itemRenders = [
    {
      id: "name",
      title: (
        <div className="flex flex-row gap-2">
          <Checkbox
            variant="admin"
            isOn={selectAll}
            onChange={handleSelectAll}
            className="w-5 h-5"
          />
          <span>Language</span>
        </div>
      ),
      headerClassName: "w-[300px]",
      render: (value, item) => {
        return (
          <div className="flex flex-row gap-2">
            <Checkbox
              variant="admin"
              isOn={selectedLanguages.includes(item.code)}
              onChange={() => handleLanguageToggle(item.code)}
              className="w-5 h-5"
            />
            <span>{item.name}</span>
          </div>
        )
      },
    },
    {
      id: "all_missing",
      title: (
        <div className="flex flex-row gap-2 items-center">
          <Checkbox
            variant="admin"
            isOn={missingOnly}
            onChange={() => setMissingOnly(!missingOnly)}
          />
          <span>Missing Only</span>
          <Tooltip
            text="Only import keys that are missing in the current language"
            position="top"
          >
            <InformationCircleIcon className="w-5 h-5 text-admin_text_secondary cursor-pointer" />
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[200px]",

      render: (value, item) => {
        return (
          <Checkbox
            variant="admin"
            isOn={value}
            onChange={(value) => handleItemMissingToggle(item, value)}
          />
        )
      },
    },
    {
      id: "existing_only",
      title: (
        <div className="flex flex-row gap-2 items-center">
          <Checkbox
            variant="admin"
            isOn={existingOnly}
            onChange={() => setExistingOnly(!existingOnly)}
          />
          <span>Existing Only</span>
          <Tooltip
            text="Only import keys that already exist in the current language"
            position="top"
          >
            <InformationCircleIcon className="w-5 h-5 text-admin_text_secondary cursor-pointer" />
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[200px]",
      render: (value, item) => {
        return (
          <Checkbox
            variant="admin"
            isOn={value}
            onChange={(value) => handleItemExistingToggle(item, value)}
          />
        )
      },
    },
    {
      id: "all",
      title: (
        <div className="flex flex-row gap-2 items-center">
          <Checkbox 
            variant="admin"
            isOn={allKeys} 
            onChange={() => setAllKeys(!allKeys)} />
          <span>All Keys</span>
          <Tooltip
            text="Import all keys in from the localization file"
            position="top"
          >
            <InformationCircleIcon className="w-5 h-5 text-admin_text_secondary cursor-pointer" />
          </Tooltip>
        </div>
      ),
      headerClassName: "w-[200px]",
      render: (value, item) => {
        return (
          <Checkbox
            variant="admin"
            isOn={value}
            onChange={(value) => handleItemAllToggle(item, value)}
          />
        )
      },
    },
  ]

  //handle the global missing only check state
  useEffect(() => {
    if (missingOnly) {
      setAllKeys(false)
      setExistingOnly(false)
    }
    //update the languages to reflect the missing only state
    const updatedLanguages = languages.map((lang) => {
      lang.all_missing = missingOnly
      if (missingOnly) {
        lang.all = false
        lang.existing_only = false
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }, [missingOnly])

  //handle the global all keys check
  useEffect(() => {
    if (allKeys) {
      setMissingOnly(false)
      setExistingOnly(false)
    }
    //update the languages to reflect the all keys state
    const updatedLanguages = languages.map((lang) => {
      lang.all = allKeys
      if (allKeys) {
        lang.all_missing = false
        lang.existing_only = false
      }

      return lang
    })
    setLanguages(updatedLanguages)
  }, [allKeys])

  useEffect(() => {
    if (existingOnly) {
      setMissingOnly(false)
      setAllKeys(false)
    }
    //update the languages to reflect the all keys state
    const updatedLanguages = languages.map((lang) => {
      lang.existing_only = existingOnly
      if (existingOnly) {
        lang.all_missing = false
        lang.all = false
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }, [existingOnly])

  // Function to handle file selection and initiate the import-stats action
  const handleFileSelect = async (event) => {
    const file = event.target.files[0]
    setFilename(file.name)
    try {
      // Call import-stats to upload and parse the file
      const response = await controller.uploadImportStats(file)

      //there was an error so nothing to do here
      if (!response) return

      //add all_missing and all keys to the response languages as true/false
      response.languages = response.languages.map((lang) => {
        lang.all_missing = false
        lang.all = false
        lang.existing_only = false
        return lang
      })

      setLanguages(response.languages) // Set languages found in the file
      setTaskProgressId(response.taskProgressId) // Store taskProgressId
      setNumberOfKeys(response.numberOfKeys) // Store total number of keys
      setSelectAll(true) // Select all languages by default
      setAllKeys(true) // Select all keys by default
    } catch (error) {
      setError("Error loading file")
      console.error("Error loading file:", error)
    }
  }

  // Function to handle the import process
  const handleImport = async () => {
    if (!filename) {
      setError("Please select a file first")
      return
    }

    if (!taskProgressId) {
      setError("No file selected for import.")
      return
    }
    
    setError("")
    setIsImporting(true)
    
    try {
      //get the languages object from languages array based on the selectedLanguages
      const payload = languages.filter((lang) =>
        selectedLanguages.includes(lang.code)
      )

      await controller.importLocalization({
        taskProgressId,
        selectedLanguages: payload,
      })

      // Import completed successfully
      setIsImporting(false)
      onClose() // Close dialog after successful import

      // Simulate progress (polling for real progress could also be implemented)
      //   const interval = setInterval(async () => {
      //     const progressData = await controller.getImportProgress(taskProgressId)
      //     setProgress(progressData.progress)
      //     if (progressData.completed) {
      //       clearInterval(interval)
      //     }
      //   }, 1000)
    } catch (error) {
      setError("Error during import")
      setIsImporting(false)
      console.error("Error during import:", error)
    }
  }

  useEffect(() => {
    setSelectedLanguages(selectAll ? languages.map((lang) => lang.code) : [])
  }, [selectAll])

  const handleItemMissingToggle = (item, value) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === item.code) {
        lang.all_missing = value
        lang.all = !lang.all_missing
        lang.existing_only = !lang.all_missing
      }
      return lang
    })

    setLanguages(updatedLanguages)
  }

  const handleItemExistingToggle = (item, value) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === item.code) {
        lang.existing_only = value
        lang.all = !lang.existing_only
        lang.all_missing = !lang.existing_only
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  const handleItemAllToggle = (item, value) => {
    const updatedLanguages = languages.map((lang) => {
      if (lang.code === item.code) {
        lang.all = value
        lang.all_missing = !lang.all
        lang.existing_only = !lang.all
      }
      return lang
    })
    setLanguages(updatedLanguages)
  }

  //reset on close
  useEffect(() => {
    if (!isOpen) {
      //reset file input
      fileInputRef?.current && (fileInputRef.current.value = "")
      setFilename("")
      setLanguages([])
      setSelectAll(false)
      setMissingOnly(false)
      setAllKeys(false)
      setSelectedLanguages([])
      setTaskProgressId(null)
      setNumberOfKeys(0)
      setProgress(0)
      setError("")
      setIsImporting(false)
    }
  }, [isOpen])

  return (
    <Modal
      variant="admin"
      className="w-[900px] max-w-[90vw]"
      isOpen={isOpen}
      onClose={onClose}
      title={`Import Localization`}
      closeOnEscape={true}
    >
      <div className="p-4 text-admin_text space-y-4">
        {/* File Selection Section */}
        {!filename && (
          <div className="text-center py-6 border-2 border-dashed border-admin_bordered rounded-lg bg-admin_paper">
            <div className="space-y-3">
              <div className="text-admin_text_secondary">
                <svg className="w-10 h-10 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="font-medium">Select a localization file to import</p>
                <p className="text-sm">Choose an XLSX file containing localization data</p>
              </div>
              <div className="flex justify-center mt-2">
                <Button
                  variant="admin_primary"
                  onClick={() => document.getElementById("fileInput").click()}
                >
                  Select XLSX File
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* File Content Section */}
        {filename && (
          <>
            <div className="bg-admin_paper p-3 rounded-lg border border-admin_bordered">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-admin_text">Selected File</div>
                  <div className="text-sm text-admin_text_secondary">{filename}</div>
                </div>
                <Button
                  variant="admin_secondary"
                  size="sm"
                  onClick={() => document.getElementById("fileInput").click()}
                >
                  Change File
                </Button>
              </div>
            </div>

            <DataTable
              localUserPreferencesKey="admin.localization.importLanguages"
              variant="admin"
              title="Languages and Import Options"
              data={languages}
              preferredHeight="250px"
              itemHeight="h-[60px]"
              rowStyleRender={(index) => {
                return index % 2 === 0
                  ? "bg-admin_canvas"
                  : "bg-admin_canvas bg-opacity-50"
              }}
              renders={itemRenders}
              showFooter={false}
              noData={
                <div className="text-center py-6 text-admin_text_secondary">
                  No languages found in the selected file
                </div>
              }
            />

            {/* Import Summary */}
            {languages?.length > 0 && (
              <div className="bg-admin_canvas p-3 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-admin_text">Selected Languages: </span>
                    <span className="text-admin_text_secondary">{selectedLanguages.length}</span>
                  </div>
                  <div>
                    <span className="font-medium text-admin_text">Total Keys: </span>
                    <span className="text-admin_text_secondary">{numberOfKeys}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Progress Section */}
        {progress > 0 && (
          <div className="space-y-2">
            <ProgressBar
              progress={progress}
              label={error ? "Import Error" : "Importing localization data..."}
            />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-admin_error/10 border border-admin_error/20 rounded-lg p-3">
            <div className="text-admin_error text-sm">{error}</div>
          </div>
        )}

        {/* Importing Spinner */}
        {isImporting && (
          <div className="flex items-center justify-center space-x-3 py-4">
            <Spinner variant="admin" size="sm" />
            <span className="text-admin_text">Importing languages...</span>
          </div>
        )}

        {/* Actions */}
        {filename && selectedLanguages.length > 0 && !isImporting && (
          <div className="flex justify-center pt-3 border-t border-admin_bordered">
            <Button 
              variant="admin_primary"
              onClick={handleImport}
              disabled={progress > 0}
            >
              Import Localization
            </Button>
          </div>
        )}

        {/* Hidden File Input */}
        <input
          id="fileInput"
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    </Modal>
  )
}
